import React, { useEffect, useState } from "react";
import { Music2, Loader2, ExternalLink } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ShareButton } from "../components/ShareButton";
import { api } from "../lib/api";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";

export default function SpotifyPage() {
  const copy = usePageContent("spotify");
  const [setting, setSetting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/settings/spotify")
      .then((r) => alive && setSetting(r.data))
      .catch(() => alive && setSetting({ playlist_id: null }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const pid = setting?.playlist_id;
  const shareUrl = setting?.url;

  return (
    <div
      className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]"
      data-testid="page-spotify"
    >
      <Header />
      <main className="mx-auto max-w-5xl px-5 pt-4 pb-16 sm:px-8 sm:pt-10 sm:pb-20">
        {copy.hide_header ? null : (
          <div className="border-b border-black/10 pb-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2 text-brand-accent" style={kickerStyle(copy)}>
                  <Music2 size={16} strokeWidth={2.5} />
                  <span className="label-kicker">{copy.kicker}</span>
                </div>
                <h1
                  className="brand-headline mt-3 whitespace-pre-line font-display text-4xl font-black tracking-tight sm:text-5xl"
                  style={titleStyle(copy)}
                >
                  {copy.title}
                </h1>
                {copy.intro ? (
                  <p className="mt-3 max-w-2xl text-base leading-relaxed text-[var(--brand-body)]">
                    {copy.intro}
                  </p>
                ) : null}
                {shareUrl ? (
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="spotify-open-external"
                    className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-3 py-2 label-kicker text-[var(--brand-ink)] hover:border-[#0A0F17]"
                  >
                    Open in Spotify <ExternalLink size={12} />
                  </a>
                ) : null}
              </div>
              <ShareButton
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={copy.title || "The Sky Blue playlist"}
              />
            </div>
          </div>
        )}

        <div className="mt-10">
          {loading ? (
            <div className="flex items-center gap-2 text-[var(--brand-body)]">
              <Loader2 size={16} className="animate-spin" /> Loading playlist…
            </div>
          ) : !pid ? (
            <div
              className="rounded-md border border-dashed border-black/15 bg-white p-10 text-center"
              data-testid="spotify-empty"
            >
              <p className="font-display text-lg font-bold">
                Playlist isn't set yet.
              </p>
              <p className="mt-1 text-sm text-[var(--brand-body)]">
                An editor needs to configure a Spotify playlist first.
              </p>
            </div>
          ) : (
            <div
              className="overflow-hidden rounded-md border border-black/10 bg-white"
              data-testid="spotify-embed"
            >
              <iframe
                title="Spotify playlist"
                src={`https://open.spotify.com/embed/playlist/${pid}?utm_source=generator&theme=0`}
                width="100%"
                height="600"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ border: 0, display: "block" }}
              />
            </div>
          )}
        </div>

        <p className="mt-6 label-kicker text-[var(--brand-body)]">
          Music streamed by Spotify. By pressing play, you agree to Spotify's
          terms — see our{" "}
          <a
            href="/page/privacy"
            className="underline decoration-[#6CABDD] underline-offset-4"
          >
            privacy notice
          </a>
          .
        </p>
      </main>
      <Footer />
    </div>
  );
}
